<template>
  <v-dialog v-model="dialog" max-width="600px">
    <v-card>
      <v-card-title>
        <span class="text-18 font-weight-bold">{{
          isEditMode ? "Editar Banco" : "Criar Banco"
        }}</span>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <!-- Logo Upload -->
          <div class="d-flex gap-3 justify-center pb-3">
            <div>
              <v-img
                @click="openFileInput('logo')"
                :src="previewImages.logo || bankData.logo || null"
                :aspect-ratio="1"
                class="rounded-lg"
                style="width: 120px; height: 120px"
                :class="{
                  'grey lighten-2': !previewImages.logo && !bankData.logo,
                }"
                contain
              >
                <template v-slot:placeholder>
                  <div
                    class="d-flex flex-grow-1 h-full justify-center flex-column align-center grey text-center"
                  >
                    <p class="mb-0 text-13 font-weight-medium">
                      Selecione a logo
                    </p>
                  </div>
                </template>
              </v-img>
            </div>
            <div class="d-flex gap-2 flex-grow-1 flex-column">
              <v-text-field
                v-model="bankData.code"
                label="Código"
                dense
                required
                :counter="3"
                outlined
                :rules="[
                  (v) => !!v || 'Código é obrigatório',
                  (v) =>
                    /^\d{3}$/.test(v) ||
                    'O código deve ter exatamente 3 dígitos numéricos',
                ]"
                :disabled="isEditMode"
              ></v-text-field>

              <v-text-field
                v-model="bankData.name"
                label="Nome"
                required
                dense
                hide-details="auto"
                outlined
                :rules="[(v) => !!v || 'Nome é obrigatório']"
              ></v-text-field>
            </div>
          </div>

          <v-textarea
            v-model="binsText"
            label="BINs (um por linha)"
            outlined
            hint="Digite um BIN de 6 dígitos por linha"
            persistent-hint
            :rules="[validateBins]"
          ></v-textarea>

          <!-- Hidden File Input -->
          <v-file-input
            v-show="false"
            v-model="fileInputs.logo"
            accept="image/*"
            @change="onImageSelected('logo')"
            ref="logoInput"
          ></v-file-input>
        </v-form>
      </v-card-text>

      <v-alert v-if="error" type="error" class="mx-3">
        {{ error }}
      </v-alert>

      <v-card-actions>
        <v-btn color="primary" text @click="close">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="save"
          :disabled="!valid"
          :loading="loading"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import banksService from "@/services/admin/banks";

export default {
  name: "BankDialog",
  props: {
    value: Boolean,
    bank: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      valid: true,
      dialog: false,
      isEditMode: false,
      loading: false,
      error: null,
      bankData: {},
      binsText: "",
      fileInputs: {
        logo: null,
      },
      previewImages: {
        logo: null,
      },
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.dialog = newVal;
      },
      immediate: true,
    },
    dialog(newVal) {
      this.$emit("input", newVal);
      if (newVal) {
        this.initializeData();
      }
    },
  },
  methods: {
    validateBins(value) {
      if (!value) return true;
      const bins = value.split("\n").filter((bin) => bin.trim());

      const invalidBins = bins.filter((bin) => !/^\d{6}$/.test(bin.trim()));
      if (invalidBins.length > 0) {
        return "Todos os BINs devem ter exatamente 6 dígitos";
      }

      const uniqueBins = new Set(bins);
      if (uniqueBins.size !== bins.length) {
        return "Existem BINs duplicados na lista. Cada BIN deve ser único.";
      }

      return true;
    },
    initializeData() {
      this.fileInputs = {
        logo: null,
      };
      if (this.bank) {
        this.isEditMode = true;
        this.bankData = JSON.parse(JSON.stringify(this.bank));
        this.binsText = JSON.parse(this.bank.bins).join("\n");
        this.setPreviewImages();
      } else {
        this.isEditMode = false;
        this.bankData = {
          code: "",
          name: "",
          logo: null,
          bins: "[]",
        };
        this.binsText = "";
        this.previewImages = {
          logo: null,
        };
      }
    },
    setPreviewImages() {
      if (this.bankData.logo) {
        this.previewImages.logo = this.bankData.logo;
      }
    },
    openFileInput(inputType) {
      this.$refs[`${inputType}Input`].$refs.input.click();
    },
    async onImageSelected(inputType) {
      const file = this.fileInputs[inputType];
      if (file) {
        try {
          const formData = new FormData();
          formData.append("file", file);

          if (this.isEditMode) {
            const response = await banksService.uploadBankImage(
              this.bankData.id,
              formData
            );
            this.bankData[inputType] = response.url;
          } else {
            // Store the file to upload after bank creation
            this.bankData[inputType] = file;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            this.previewImages[inputType] = e.target.result;
          };
          reader.readAsDataURL(file);
        } catch (error) {
          this.error = `Erro ao fazer upload da imagem: ${error.message}`;
        }
      }
    },
    close() {
      this.dialog = false;
      this.$emit("input", false);
      this.$refs.form.reset();
      this.fileInputs = {
        logo: null,
      };
      this.bankData = {
        code: "",
        name: "",
        logo: null,
        bins: "[]",
      };
      this.binsText = "";
      this.previewImages = {
        logo: null,
      };
      this.error = null;
      this.valid = true;
      this.isEditMode = false;
    },
    async save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          const bankData = {
            code: this.bankData.code,
            name: this.bankData.name,
            bins: JSON.stringify(
              Array.from(
                new Set( // Remove duplicatas
                  this.binsText
                    .split("\n")
                    .map((bin) => bin.trim())
                    .filter((bin) => /^\d{6}$/.test(bin))
                )
              )
            ),
          };

          let response;

          if (this.isEditMode) {
            response = await banksService.updateBank(
              this.bankData.id,
              bankData
            );
          } else {
            // Primeiro fazer upload da imagem se existir
            response = await banksService.createBank(bankData);

            if (this.bankData.logo instanceof File) {
              const formData = new FormData();
              formData.append("file", this.bankData.logo);
              try {
                const uploadResponse = await banksService.uploadBankImage(
                  response.bankId,
                  "logo",
                  formData
                );
                bankData.logo = uploadResponse.url;
              } catch (error) {
                this.error =
                  "Erro ao fazer upload do logo. Por favor, tente novamente.";
                this.loading = false;
                return;
              }
            }
          }

          const updatedBank = {
            ...this.bankData,
            id: response.bankId || this.bankData.id,
            logo: this.isEditMode ? this.previewImages.logo : bankData.logo,
            bins: bankData.bins,
          };

          this.close();
          this.$emit("bank-saved", updatedBank);
        } catch (error) {
          console.error(error);
          this.error = error.message || "Erro ao salvar banco";
        } finally {
          this.loading = false;
        }
      }
    },
    openDialog() {
      this.dialog = true;
    },
  },
};
</script>
